import type { ReactNode } from 'react';
import { Button, ButtonRow, Card } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import type { IRequest } from '../../../../common/interfaces/IRequest';
import type { IAuthenticatedUser } from '../../../../common/utils/IAuthenticatedUser';
import type {
    IGtoProcedureAddValidated,
    IGtoProcedureCustomControlData,
} from '../../../../common/validation/customControls/general';
import type { IReduxState } from '../../../configureStore';
import type { DispatchType } from '../../../store/utils/dispatchType';
import type { ValidationErrors } from '../../../utils/validation';
import { ServiceCode } from '../../../../common/enums/ServiceCode';
import {
    gtoProcedureSchema,
    step1Schema,
    step2Schema,
    step3NoGPInvitationSchema,
    step3Schema,
    step4Schema,
} from '../../../../common/validation/customControls/general';
import { receiveUserData } from '../../../store/authentication/actions';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { isPendingRequestForService } from '../../../utils/isPendingRequestForService';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { ParagraphTitle } from '../../elements/atoms/paragraphTitle/ParagraphTitle';
import { Article } from '../../elements/molecules/article/article';
import { Container } from '../../elements/molecules/container/Container';
import { Main } from '../../elements/molecules/main/Main';
import { PageSection } from '../../elements/molecules/pageSection/PageSection';
import { Footer } from '../../elements/organisms/footer/Footer';
import { Header } from '../../elements/organisms/header/Header';
import { PageWrapper } from '../../elements/organisms/pageWrapper/PageWrapper';
import { RequestSentMessage } from './steps/RequestSent';
import { Step1 } from './steps/Step1';
import { Step2 } from './steps/Step2';
import { Step3 } from './steps/Step3';
import { Step4 } from './steps/Step4';
import { Step5 } from './steps/Step5';

export interface IStepProps {
    errors: ValidationErrors;
    handleChange: (update: Partial<IGtoProcedureCustomControlData>) => void;
    isSubmitting?: boolean;
    onUploadStatusChanged?: (isUploading: boolean) => void;
    request: IGtoProcedureAddValidated;
    submit?: () => void;
    validateAndGoToNextStep: () => void;
}

interface INewRequestReduxButtonProps {
    canCreateGhcConsult: boolean;
    pendingGtoRequest?: IRequest;
    processesLoaded: boolean;
}

const mapStateToProps = ({ authentication }: IReduxState): INewRequestReduxButtonProps => ({
    canCreateGhcConsult: authentication.permissions?.canCreateGhcConsult || false,
    pendingGtoRequest: authentication.requestProcesses?.find((request: IRequest) =>
        isPendingRequestForService(request, ServiceCode.GTO)
    ),
    processesLoaded: !!authentication.requestProcesses,
});

type NewRequestProps = INewRequestReduxButtonProps & ReturnType<typeof mapDispatchToProps>;

function NewGhcRequestWrapper(props: NewRequestProps) {
    const [request, setRequest] = useState<IGtoProcedureAddValidated>({
        customControlData: {
            areThereAdditionalComplaintsOrSymptons: '',
            areThereAdditionalComplaintsOrSymptonsRemark: '',
            areYouBeingTreated: '',
            areYouBeingTreatedRemark: '',
            areYouOnMedication: '',
            areYouOnMedicationRemark: '',
            complaints: '',
            complaintsDuration: '',
            complaintsLocation: '',
            complaintsSeverity: '',
            doYouHaveFever: '',
            doYouHaveFeverRemark: '',
            email: '',
            files: [],
            haveYouEverBeenHospitalized: '',
            haveYouEverBeenHospitalizedRemark: '',
            phoneNumber: '',
        },
    });

    const [currentStep, setCurrentStep] = useState<number>(0);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [areValidationsActivated, setAreValidationsActivated] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        const procedureId = props.pendingGtoRequest?.procedure?.id;
        setRequest({
            ...request,
            customControlData: {
                ...request.customControlData,
                areYouBeingTreated: procedureId ? undefined : '',
                areYouBeingTreatedRemark: procedureId ? undefined : '',
                areYouOnMedication: procedureId ? undefined : '',
                areYouOnMedicationRemark: procedureId ? undefined : '',
                email: procedureId ? undefined : '',
                organizationName: procedureId ? undefined : '',
                practitionerName: procedureId ? undefined : '',
            },
            procedureId,
        });
    }, [props.pendingGtoRequest]);

    useEffect(() => {
        if (areValidationsActivated) {
            validateOnChange();
        }
    }, [request]);

    const getValidationSchemaByStep = (step: number) => {
        switch (step) {
            case 0:
                return step1Schema;
            case 1:
                return step2Schema;
            case 2:
                return props.pendingGtoRequest ? step3Schema : step3NoGPInvitationSchema;
            case 3:
                return step4Schema;
            default:
                throw new Error('Schema not defined for step ' + step);
        }
    };

    const validateAndGoToNextStep = () => {
        setAreValidationsActivated(true);
        const schema = getValidationSchemaByStep(currentStep);
        const validationResult = validate(schema, request.customControlData);
        if (validationResult.errors.size === 0) {
            goToNextStep();
        } else {
            setErrors(validationResult.errors);
        }
    };

    const validateOnChange = () => {
        const schema = getValidationSchemaByStep(currentStep);
        const validationResult = validate(schema, request.customControlData);
        setErrors(validationResult.errors);
    };

    const goToPrevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const goToNextStep = () => {
        setAreValidationsActivated(false);
        setErrors(emptyValidationErrors);
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleChange = (update: Partial<IGtoProcedureCustomControlData>) => {
        setRequest({ ...request, customControlData: { ...request.customControlData, ...update } });
    };

    const submitRequest = async () => {
        setIsSubmitting(true);
        const validationResult = validate(gtoProcedureSchema, request);
        if (validationResult.errors.size === 0) {
            const result = await fetchJson(
                `/api/patient/homeExamination?serviceCode=${ServiceCode.GTO}`,
                'POST',
                getPostData()
            );
            goToNextStep();
            if (result.json && result.json.patientData) {
                props.receiveUserData(result.json.patientData);
            }
        } else {
            setIsSubmitting(false);
        }
    };

    const getPostData = () => {
        const result = JSON.parse(JSON.stringify(request));
        if (result.customControlData.files && result.customControlData.files.length > 0) {
            for (const file of result.customControlData.files) {
                delete file.thumbnailDataUrl;
            }
        }

        return { procedure: result };
    };

    const showBackButton = () => {
        return currentStep > 0 && currentStep < steps.length - 1 ? (
            <Button
                color="secondary"
                isDisabled={isUploading || isSubmitting}
                onClick={goToPrevStep}
                type="button"
                variant="link"
            >
                &#60; Vorige
            </Button>
        ) : null;
    };

    const stepProps = {
        errors,
        handleChange,
        request,
        validateAndGoToNextStep,
    };

    const steps: ReactNode[] = [
        <Step1
            key={0}
            {...stepProps}
        />,
        <Step2
            key={1}
            {...stepProps}
            onUploadStatusChanged={(uploadingStatus) => setIsUploading(uploadingStatus)}
        />,
        <Step3
            key={2}
            {...stepProps}
        />,
    ];
    if (!props.pendingGtoRequest) {
        steps.push(<Step4 {...stepProps} />);
    }
    steps.push(
        <Step5
            {...stepProps}
            isSubmitting={isSubmitting}
            submit={submitRequest}
        />,
        <RequestSentMessage {...stepProps} />
    );

    if (props.canCreateGhcConsult) {
        return (
            <PageWrapper
                fullHeight
                scrollToTopUpdater={currentStep}
            >
                <Header
                    backButtonText={translateKey('common.home')}
                    backUrl="/"
                    hasBackbutton={currentStep === 0}
                />
                <Main>
                    <Container isFluidOnMobile>
                        <PageSection>
                            {showBackButton()}
                            <Article>
                                <ParagraphTitle className="mb-0">
                                    {currentStep < steps.length - 1 ? (
                                        <>
                                            Consult aanvraag{' '}
                                            <small>
                                                (stap {currentStep + 1} van {steps.length - 1})
                                            </small>
                                        </>
                                    ) : (
                                        'Het is gelukt'
                                    )}
                                </ParagraphTitle>
                            </Article>
                        </PageSection>
                        <Card>
                            {currentStep >= 0 && currentStep < steps.length && steps[currentStep]}
                            <ButtonRow justify="center">{showBackButton()}</ButtonRow>
                        </Card>
                    </Container>
                </Main>
                <Footer />
            </PageWrapper>
        );
    } else {
        return null;
    }
}

function mapDispatchToProps(dispatch: DispatchType) {
    return {
        dispatch,
        receiveUserData: (userData: Partial<IAuthenticatedUser>) => {
            dispatch(receiveUserData(userData));
        },
    };
}

export const NewGhcRequest = connect(mapStateToProps, mapDispatchToProps)(NewGhcRequestWrapper);
